// DS: CR55
/*  The top portion of a single topic page containing a hero image
 *  and associated links.
 */
import React, { useState, useContext, useCallback } from 'react';
import classnames from 'classnames';
import { useTranslation } from 'react-i18next';
import { PapiTopic } from '@wix/da-papi-types';
import { useScrollAware } from '@wix/da-hooks/pkg/useScrollAware';
import { Section } from '@wix/da-a11y/pkg/components/headings/tagWrappers';
import Heading from '@wix/da-a11y/pkg/components/headings/Heading';
import { MobileContext } from '@wix/da-react-context/pkg/MobileContext';
import { useFlag } from '@wix/da-react-context/pkg/flags/hooks';
import { BiEventType } from '@wix/da-bi/pkg/constants';
import type { BIEventPayload } from '@wix/da-bi/pkg/types';
import BiLink from '@wix/da-bi/pkg/Components/BiLink';
import Flex from '@wix/da-ds/pkg/Flex';
import ArtStageArrowL from '@wix/da-ds/pkg/Icons/ArtStageArrowL';
import shortenNumber from '@wix/da-ds/pkg/utils/shortenNumber';
import { lerp, clamp } from '@wix/da-shared-react/pkg/utils/math';
import RelatedTopics from '../RelatedTopics';
import ArtBackdrop from '../ArtBackdrop';
import TopicActions from './_partials/TopicActions';

import s from './TopicHeader.scss';

const topicHeaderHeight = parseInt(s['topic-header-height'], 10) || 48;
const HALF_HEADER_HEIGHT = topicHeaderHeight / 2;
// 30?: bump this transition pt. back a bit so it unfurls closer to top of deviations
const TOP_OF_DEVIATIONS = topicHeaderHeight - 30;

export interface Props {
  /** the topic being explored */
  topic: PapiTopic;
  /** an array of topics related to the current one */
  relatedTopics?: PapiTopic[];
  /** optional count of deviations that fall into this topic */
  estTotal?: number;
}

export const TopicHeader: React.FC<Props> = props => {
  const { topic, relatedTopics, estTotal } = props;
  const { t } = useTranslation();
  const { artStyle, infoStyle, titleStyle, pastHeader } = useTweenStyles();
  const topic_llt = useFlag('topic_llt');

  const { name, description, typeId, itemId, exampleDeviations } = topic;

  const backgroundImage = exampleDeviations?.[0];

  const topicBiData: BIEventPayload = { typeid: typeId, itemid: itemId };
  if (!name || !description) {
    return null;
  }

  return (
    <>
      <header className={s['header']}>
        {backgroundImage ? (
          <ArtBackdrop
            artStyle={artStyle}
            artClassName={s['background-art']}
            attributionClassName={s['background-attribution']}
            deviation={backgroundImage}
          />
        ) : (
          <div className={s['missing-bg']} />
        )}
        <BackToAllTopics extraBiData={topicBiData} />

        <Flex className={s['topic-main']} justifyContent="space-between">
          <div className={s['topic-info']} style={infoStyle}>
            <h1 className={s['title']} style={titleStyle}>
              {name}
            </h1>
            <p className={s['description']}>
              {description}
              {estTotal && (
                <span className={s['results']}>
                  {' / '}
                  <em>{shortenNumber(estTotal)}</em>
                  {t('topicPage.topicHeader.results', {
                    count: estTotal,
                  })}
                </span>
              )}
            </p>

            <RelatedTopics
              className={s['related-topics']}
              topics={relatedTopics}
            />
          </div>
          {topic_llt && <TopicActions topic={topic} />}
        </Flex>
      </header>

      <Section
        className={classnames(s['sticky'], pastHeader && s['shown'])}
        // Screen readers already have access to this content
        aria-hidden={true}
      >
        <BackToAllTopics extraBiData={topicBiData} />
        <Heading className={s['title']}>{name}</Heading>
      </Section>
    </>
  );
};
TopicHeader.displayName = 'TopicHeaderCR55';
export default TopicHeader;

/** Link back to the list page */
const BackToAllTopics: React.FC<{ extraBiData?: BIEventPayload }> = ({
  extraBiData = {},
}) => {
  const { t } = useTranslation();
  const biData = {
    evid: BiEventType.BACK_TO_CLICK,
    ...extraBiData,
  };

  return (
    <BiLink className={s['back-to-topics']} href="/topic" biData={biData}>
      <ArtStageArrowL customSizable className={s['back-to-topics-arrow']} />
      {t('topicListPage.allTopics')}
    </BiLink>
  );
};
BackToAllTopics.displayName = 'BackToAllTopics';

/** Interpolate header styles based on a [0, 1.0] value */
const getOpacity = (value: number) => lerp(value, 1, 0.3);
const getBgScale = (value: number) => lerp(value, 1, 1.05);
const getBgBlur = (value: number) => lerp(value, 0, 4);
const getTitleScale = (value: number) => lerp(value, 1, 0.95);

function useTweenStyles() {
  const isMobile = useContext(MobileContext);
  // TODO: this should be more performant with react-spring's animated components
  const [opacity, setOpacity] = useState(getOpacity(0));
  const [bgScale, setBgScale] = useState(getBgScale(0));
  const [bgBlur, setBgBlur] = useState(getBgBlur(0));
  const [titleScale, setTitleScale] = useState(getTitleScale(0));
  const [pastHeader, setPastHeader] = useState(false);

  const scrollHandler = useCallback(
    ({ scrollTop }) => {
      // interpolate some css properties based on the scroll distance
      // from the start of the header to its end
      let scrollDistance = isMobile
        ? scrollTop / HALF_HEADER_HEIGHT
        : scrollTop / topicHeaderHeight;
      scrollDistance = clamp(scrollDistance, 0, 1);
      setOpacity(getOpacity(scrollDistance));
      setBgScale(getBgScale(scrollDistance));
      setBgBlur(getBgBlur(scrollDistance));
      setTitleScale(getTitleScale(scrollDistance));
      setPastHeader(scrollTop >= TOP_OF_DEVIATIONS);
    },
    [isMobile, setOpacity, setBgScale, setBgBlur, setTitleScale, setPastHeader]
  );
  useScrollAware(scrollHandler);

  return {
    artStyle: {
      opacity,
      filter: `blur(${bgBlur}px)`,
      transform: `scale(${bgScale})`,
    },
    infoStyle: {
      opacity,
    },
    titleStyle: {
      transform: `scale(${titleScale})`,
    },
    pastHeader,
  };
}
